import React, { useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Stack,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
    Checkbox,
    Typography
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { createLeave, uploadMedicalCert } from '../../../services/LeaveService'

const LeaveRegisterModal = ({ open, onClose, onRegisterSuccess }) => {
    const [errors, setErrors] = useState({})
    const [responseMessage, setResponseMessage] = useState('')
    const [showResponseDialog, setShowResponseDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [newLeave, setNewLeave] = useState({
        type: '',
        reason: '',
        start: null,
        end: null,
        halfDay: '',
        fileUrl: null,
        includeHalfDay: false // New state for checkbox
    })

    const [medicalCert, setMedicalCert] = useState(null)

    // const validateFields = () => {
    //     const newErrors = {}
    //     const requiredFields = ['type', 'reason', 'start']

    //     if (!newLeave.includeHalfDay) {
    //         requiredFields.push('end')
    //     }

    //     requiredFields.forEach((field) => {
    //         if (!newLeave[field]) {
    //             newErrors[field] = 'This field is required'
    //         }
    //     })

    //     setErrors(newErrors)
    //     return Object.keys(newErrors).length === 0
    // }

    const validateFields = () => {
        const newErrors = {}
        const requiredFields = ['type', 'reason', 'start']

        if (!newLeave.includeHalfDay) {
            requiredFields.push('end')
        }

        requiredFields.forEach((field) => {
            if (!newLeave[field]) {
                newErrors[field] = 'This field is required'
            }
        })

        // ✅ Additional validation for Sick Leave
        if (newLeave.type === 'SICK LEAVE' && newLeave.start && (newLeave.end || newLeave.includeHalfDay)) {
            let endDate = newLeave.includeHalfDay ? newLeave.start : newLeave.end
            const diffTime = endDate.getTime() - newLeave.start.getTime()
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // inclusive of start date

            if (diffDays >= 3 && !medicalCert) {
                newErrors.medicalCert = 'Medical Certificate is required for 3 or more days of sick leave'
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleRegisterInputChange = (event) => {
        const { name, value, checked } = event.target

        setNewLeave((prevLeave) => {
            let updatedLeave = { ...prevLeave, [name]: value }

            if (name === 'type') {
                updatedLeave.start = null
                updatedLeave.end = null
            }
            if (name === 'includeHalfDay') {
                updatedLeave.includeHalfDay = checked
                if (checked) {
                    updatedLeave.end = updatedLeave.start || null // Set end same as start
                } else {
                    // Clear half-day values when checkbox is unchecked
                    updatedLeave.halfDay = 'NONE'
                    updatedLeave.day = 'NONE'
                }
            }
            if (name === 'halfDay') {
                const [halfDay, day] = value.split('|')
                updatedLeave.halfDay = halfDay || null
                updatedLeave.day = day || null
            }
            return updatedLeave
        })
    }

    const handleFormSubmit = async (event) => {
        event.preventDefault()
        if (loading) return

        setLoading(true)

        if (validateFields()) {
            try {
                let uploadedFileUrl = null
                if (medicalCert) {
                    const uploadResult = await uploadMedicalCert(medicalCert)
                    uploadedFileUrl = uploadResult.fileUrl || uploadResult.path
                    newLeave.fileUrl = uploadedFileUrl
                }

                const result = await createLeave(newLeave)
                setResponseMessage(result.error || result.message)
                setShowResponseDialog(true)

                if (!result.error) {
                    onRegisterSuccess()
                    resetForm()
                }
            } catch (error) {
                setResponseMessage('An error occurred while processing the leave request.')
                setShowResponseDialog(true)
            } finally {
                setLoading(false)
                onClose()
            }
        } else {
            setLoading(false)
        }
    }
    const resetForm = () => {
        setNewLeave({
            type: '',
            reason: '',
            start: null,
            end: null,
            halfDay: '',
            fileUrl: null,
            includeHalfDay: false
        })
        setMedicalCert(null)
        setErrors({})
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Leave Application Form</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleFormSubmit}>
                        <Stack spacing={2}>
                            <FormControl fullWidth error={Boolean(errors.type)}>
                                <InputLabel>Leave Type</InputLabel>
                                <Select
                                    name="type"
                                    value={newLeave.type}
                                    onChange={handleRegisterInputChange}
                                    label="Leave Type"
                                >
                                    <MenuItem value="VACATION LEAVE">VACATION LEAVE</MenuItem>
                                    <MenuItem value="SICK LEAVE">SICK LEAVE</MenuItem>
                                    <MenuItem value="EMERGENCY LEAVE">EMERGENCY LEAVE</MenuItem>
                                    <MenuItem value="MATERNITY LEAVE">MATERNITY LEAVE</MenuItem>
                                    <MenuItem value="PATERNITY LEAVE">PATERNITY LEAVE</MenuItem>
                                    <MenuItem value="UNPAID LEAVE">ABSENT</MenuItem>
                                </Select>
                                {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                            </FormControl>

                            <TextField
                                name="reason"
                                label="Reason"
                                fullWidth
                                value={newLeave.reason}
                                onChange={handleRegisterInputChange}
                                error={Boolean(errors.reason)}
                                helperText={errors.reason}
                            />

                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Start Date"
                                    value={newLeave.start}
                                    minDate={
                                        newLeave.type === 'VACATION LEAVE'
                                            ? new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
                                            : null
                                    }
                                    onChange={(start) =>
                                        handleRegisterInputChange({
                                            target: { name: 'start', value: start }
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            error={Boolean(errors.start)}
                                            helperText={errors.start}
                                        />
                                    )}
                                />
                            </LocalizationProvider>

                            {!newLeave.includeHalfDay && (
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="End Date"
                                        value={newLeave.end}
                                        minDate={newLeave.start || new Date()}
                                        onChange={(end) =>
                                            handleRegisterInputChange({
                                                target: { name: 'end', value: end }
                                            })
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={Boolean(errors.end)}
                                                helperText={errors.end}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newLeave.includeHalfDay}
                                        onChange={handleRegisterInputChange}
                                        name="includeHalfDay"
                                    />
                                }
                                label="Include Half-Day Option"
                            />

                            {newLeave.includeHalfDay && (
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Half-Day Option</FormLabel>
                                    <RadioGroup
                                        name="halfDay"
                                        value={`${newLeave.halfDay || ''}|${newLeave.day || ''}`}
                                        onChange={handleRegisterInputChange}
                                    >
                                        <FormControlLabel value="AM|FD" control={<Radio />} label="Morning Half-Day" />
                                        {/* <FormControlLabel
                                            value="AM|LD"
                                            control={<Radio />}
                                            label="Morning Half-Day (Last Day)"
                                        /> */}
                                        <FormControlLabel
                                            value="PM|FD"
                                            control={<Radio />}
                                            label="Afternoon Half-Day"
                                        />
                                        {/* <FormControlLabel
                                            value="PM|LD"
                                            control={<Radio />}
                                            label="Afternoon Half-Day (Last Day)"
                                        /> */}
                                    </RadioGroup>
                                </FormControl>
                            )}
                            {newLeave.type === 'SICK LEAVE' && (
                                <>
                                    <Typography variant="subtitle2">
                                        Upload Medical Certificate (required if greater than or equal 3 working days)
                                    </Typography>
                                    <Button variant="outlined" component="label">
                                        {medicalCert ? medicalCert.name : 'Choose File'}
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*,application/pdf"
                                            onChange={(e) => setMedicalCert(e.target.files[0])}
                                        />
                                    </Button>
                                    {errors.medicalCert && <FormHelperText error>{errors.medicalCert}</FormHelperText>}
                                </>
                            )}
                        </Stack>

                        <DialogActions>
                            <Button onClick={onClose} color="secondary">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained" disabled={loading}>
                                {loading ? 'Applying...' : 'Apply'}
                            </Button>
                        </DialogActions>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={showResponseDialog} onClose={() => setShowResponseDialog(false)}>
                <DialogTitle>Response</DialogTitle>
                <DialogContent>
                    <p>{responseMessage}</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowResponseDialog(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default LeaveRegisterModal
